/**
 * RoleNavigationPage Component
 *
 * Displays organisation and role selection for users with
 * multiple organisations or roles.
 *
 * This page is shown after authentication when:
 * - User belongs to multiple organisations
 * - User has multiple roles in a single organisation
 * - User is an admin AND has org memberships
 */

import { useState, useMemo } from 'react';
import { useAuth } from '../auth';
import { useUserOrganisations } from '../hooks';
import { OrganisationCard } from '../components/OrganisationCard';
import { Loading } from '../components';
import heroBg1 from '../../../common/assets/images/hero-construction-1.jpg';
import heroBg4 from '../../../common/assets/images/hero-construction-4.jpg';
import './RoleNavigationPage.css';

interface ActivityItem {
  id: string;
  label: string;
  detail: string;
  role: string;
  path: string;
  orgSlug?: string;
}

/**
 * Get background image URL based on current time
 * 9:00-16:00 (business hours): hero-construction-1.jpg
 * Outside business hours: hero-construction-4.jpg
 */
function getBackgroundImage(): string {
  const currentHour = new Date().getHours();
  const isDayTime = currentHour >= 9 && currentHour < 16;
  const imageUrl = isDayTime ? heroBg1 : heroBg4;
  return `url('${imageUrl}')`;
}

/**
 * RoleNavigationPage Component
 *
 * Shows a selection interface for users with multiple organisations/roles.
 * Handles navigation to downstream apps with token passing.
 */
export function RoleNavigationPage() {
  const { user, logout } = useAuth();
  const { isLoading, error, decision } = useUserOrganisations();
  const [isRedirecting, setIsRedirecting] = useState(false);
  const backgroundImage = useMemo(() => getBackgroundImage(), []);

  const baseUrls: Record<string, string> = {
    admin: import.meta.env.VITE_ADMIN_APP_URL || 'https://admin.hirenow.com',
    partner: import.meta.env.VITE_PARTNER_APP_URL || 'https://partner.hirenow.com',
    supplier:
      import.meta.env.VITE_SUPPLIER_APP_URL || 'https://supplier.hirenow.com',
    hirer: import.meta.env.VITE_HIRER_APP_URL || 'https://hirer.hirenow.com',
  };

  const buildTargetUrl = (role: string, orgSlug?: string, path = ''): string => {
    const baseUrl = baseUrls[role.toLowerCase()] || baseUrls.hirer;
    const orgPrefix = orgSlug ? `/${orgSlug}` : '';
    const normalisedPath = path.startsWith('/') ? path : `/${path}`;
    const targetUrl = `${baseUrl}${orgPrefix}${normalisedPath}`;

    if (user?.idToken) {
      return `${targetUrl}#token=${encodeURIComponent(user.idToken)}`;
    }

    return targetUrl;
  };

  /**
   * Handle role selection - navigates to the appropriate app
   */
  const handleSelectRole = (orgSlug: string, role: string) => {
    setIsRedirecting(true);
    window.location.href = buildTargetUrl(role, orgSlug);
  };

  /**
   * Handle admin-only access (no org context needed)
   */
  const handleAdminAccess = () => {
    setIsRedirecting(true);
    window.location.href = buildTargetUrl('admin');
  };

  /**
   * Handle logout
   */
  const handleLogout = async () => {
    await logout();
    window.location.href = '/';
  };

  // Selection-derived values must be resolved before early returns
  // so hook order remains stable across all render paths.
  const organisations =
    decision?.type === 'show-selection' ? decision.organisations : [];
  const isAdmin =
    decision?.type === 'show-selection' ? decision.isAdmin : false;

  const activityItems: ActivityItem[] = useMemo(() => {
    const items: ActivityItem[] = [];

    organisations.forEach((org) => {
      if (org.roles.includes('hirer')) {
        items.push({
          id: `${org.organisation_id}-hirer`,
          label: `${org.name}: request updates`,
          detail: 'Review requests needing follow-up',
          role: 'hirer',
          orgSlug: org.slug,
          path: '/requests?tab=active',
        });
      }

      if (org.roles.includes('partner')) {
        items.push({
          id: `${org.organisation_id}-partner`,
          label: `${org.name}: allocation updates`,
          detail: 'Open current jobs and allocation changes',
          role: 'partner',
          orgSlug: org.slug,
          path: '/jobs?tab=allocations',
        });
      }

      if (org.roles.includes('supplier')) {
        items.push({
          id: `${org.organisation_id}-supplier`,
          label: `${org.name}: supplier actions`,
          detail: 'Check incoming jobs and confirmations',
          role: 'supplier',
          orgSlug: org.slug,
          path: '/jobs?tab=incoming',
        });
      }
    });

    if (isAdmin) {
      items.unshift({
        id: 'platform-admin-review',
        label: 'Platform administration',
        detail: 'View pending approvals and system alerts',
        role: 'admin',
        path: '/?tab=alerts',
      });
    }

    return items.slice(0, 5);
  }, [isAdmin, organisations]);

  // Loading state
  if (isLoading) {
    return <Loading fullScreen message="Loading your organisations..." />;
  }

  // Redirecting state
  if (isRedirecting) {
    return <Loading fullScreen message="Redirecting..." />;
  }

  // Error state
  if (error) {
    return (
      <div className="nav-page nav-page-error">
        <h1>Something went wrong</h1>
        <p>{error.message}</p>
        <button
          onClick={() => window.location.reload()}
          className="nav-btn-primary"
        >
          Try Again
        </button>
        <button onClick={handleLogout} className="nav-btn-secondary">
          Sign Out
        </button>
      </div>
    );
  }

  // No access state
  if (decision?.type === 'no-access') {
    return (
      <div className="nav-page nav-page-error">
        <h1>No Access</h1>
        <p>{decision.reason}</p>
        <button onClick={handleLogout} className="nav-btn-secondary">
          Sign Out
        </button>
      </div>
    );
  }

  return (
    <div className="nav-page" style={{ backgroundImage }}>
      <header className="nav-header">
        <div className="nav-logo">
          <img src="/favicon-hirenow.svg" alt="Hire It Now" height="32" />
        </div>
        <button onClick={handleLogout} className="nav-logout-btn">
          Sign Out
        </button>
      </header>

      <main className="nav-content">
        <h1 className="nav-title">
          Welcome{user?.displayName ? `, ${user.displayName}` : ''}!
        </h1>
        <p className="nav-subtitle">Select where you'd like to go:</p>

        <div className="nav-cards">
          {/* Admin access card (if admin) */}
          {isAdmin && (
            <div className="org-card org-card-admin">
              <h3 className="org-card-title">Platform Administration</h3>
              <p className="org-card-slug">System-wide access</p>
              <div className="org-card-roles">
                <button
                  className="role-button role-admin"
                  onClick={handleAdminAccess}
                  disabled={isRedirecting}
                >
                  <span className="role-icon" aria-hidden="true">
                    ⚙️
                  </span>
                  <span className="role-label">Admin Console</span>
                </button>
              </div>
            </div>
          )}

          {/* Organisation cards */}
          {organisations.map((org) => (
            <OrganisationCard
              key={org.organisation_id}
              organisation={org}
              onSelectRole={handleSelectRole}
              isLoading={isRedirecting}
            />
          ))}
        </div>

        {activityItems.length > 0 && (
          <section className="nav-activity-card" aria-label="Recent activity">
            <h2 className="nav-activity-title">Recent Activity</h2>
            <p className="nav-activity-subtitle">
              Quick links to records that may need your attention.
            </p>
            <ul className="nav-activity-list">
              {activityItems.map((item) => (
                <li key={item.id} className="nav-activity-item">
                  <a
                    className="nav-activity-link"
                    href={buildTargetUrl(item.role, item.orgSlug, item.path)}
                    onClick={() => setIsRedirecting(true)}
                  >
                    <span className="nav-activity-label">{item.label}</span>
                    <span className="nav-activity-detail">{item.detail}</span>
                  </a>
                </li>
              ))}
            </ul>
          </section>
        )}
      </main>

      <footer className="nav-footer">
        <p>
          Need help?{' '}
          <a href="mailto:support@hirenow.com">Contact Support</a>
        </p>
      </footer>
    </div>
  );
}
